import { useEffect, useState } from "react";

const KEY = (screen) => `sail.sort.${screen}`;
const DAY = /^(\d{2})[./-](\d{2})[./-](\d{4})$/;

// The portal writes a day as 24.03.2027. Compared as text that lands between March and June, so a
// day is turned round to 2027-03-24 before two of them are weighed against each other.
const asComparable = (value) => {
  if (typeof value !== "string") return value;
  const day = value.match(DAY);
  return day ? `${day[3]}-${day[2]}-${day[1]}` : value;
};

/** Puts rows in order. Blanks sit last whichever way a column runs — an empty cell is not "least". */
export const sortRows = (rows, key, dir, pick) => {
  if (!key || !Array.isArray(rows)) return rows;
  const way = dir === "desc" ? -1 : 1;
  const value = (row) => (pick ? pick(row, key) : row?.[key]);
  const blank = (v) =>
    v === null || v === undefined || v === "" || v === "—" || v === "-" || Number.isNaN(v);
  return [...rows].sort((a, b) => {
    const x = value(a);
    const y = value(b);
    if (blank(x) && blank(y)) return 0;
    if (blank(x)) return 1;
    if (blank(y)) return -1;
    if (typeof x === "number" && typeof y === "number") return (x - y) * way;
    if (typeof x === "boolean" && typeof y === "boolean") return (Number(x) - Number(y)) * way;
    return (
      String(asComparable(x)).localeCompare(String(asComparable(y)), "en", {
        numeric: true,
        sensitivity: "base",
      }) * way
    );
  });
};

/** A figure to sort by, or nothing at all — so an empty cell is never read as a zero. */
export const asNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) && n !== 0 ? n : n === 0 ? 0 : null;
};

/** The column a screen is sorted by, remembered on this machine so it is still there tomorrow. */
export function useSort(screen, fallback = { key: "", dir: "asc" }) {
  const [sort, setSort] = useState(() => {
    try {
      const kept = JSON.parse(window.localStorage.getItem(KEY(screen)) || "null");
      if (kept && typeof kept.key === "string") return kept;
    } catch {
      /* nothing kept, or unreadable — the screen's own order stands */
    }
    return fallback;
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(KEY(screen), JSON.stringify(sort));
    } catch {
      /* a machine that refuses storage still sorts, it just forgets */
    }
  }, [screen, sort]);

  // One tap sorts a column, another turns it around, a third puts the screen back on its own order.
  const on = (key) =>
    setSort((s) =>
      s.key !== key
        ? { key, dir: "asc" }
        : s.dir === "asc"
          ? { key, dir: "desc" }
          : { key: "", dir: "asc" },
    );

  return { sort, on, set: setSort };
}
